import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './super-admin.css';
import { Card, Col, Row, Typography, Statistic, Progress, Table, Tag, Space, Button, Select } from 'antd';
import { TeamOutlined, GlobalOutlined, AlertOutlined, CodeOutlined, CheckCircleOutlined, BugOutlined, ReloadOutlined, BarChartOutlined } from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../config/api';

const { Title, Text } = Typography;

const SADashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [usersCount, setUsersCount] = useState(0);
  const [websitesCount, setWebsitesCount] = useState(0);
  const [openTicketsCount, setOpenTicketsCount] = useState(0);
  const [activeSnippetsCount, setActiveSnippetsCount] = useState(0);
  const [activity, setActivity] = useState([]);
  const [topWebsites, setTopWebsites] = useState([]);
  const [uptimePercent, setUptimePercent] = useState(null);
  const [errorsToday, setErrorsToday] = useState(0);
  const [apiHealth, setApiHealth] = useState({ ok: true, latencyMs: null });
  const [dbHealth, setDbHealth] = useState({ ok: false, latencyMs: null });
  const [websiteAnalytics, setWebsiteAnalytics] = useState([]);
  const [selectedWebsiteType, setSelectedWebsiteType] = useState('all');

  const load = React.useCallback(async () => {
      setLoading(true);
      try {
        // Users
        try {
          const res = await fetch(`${apiBase}/admin/users`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
          const body = await res.json().catch(() => ([]));
          if (res.ok && Array.isArray(body)) setUsersCount(body.length);
        } catch {}

        // Websites (clients with websites)
        try {
          const params = new URLSearchParams({ limit: '200', offset: '0' });
          const res = await fetch(`${apiBase}/admin/clients-websites?${params}`, { headers: { ...authHeaders() } });
          const body = await res.json().catch(() => ({ clients: [] }));
          // Try to infer websites count
          if (res.ok) {
            const clients = Array.isArray(body?.clients) ? body.clients : (Array.isArray(body) ? body : []);
            // Count aggregated websites arrays if present
            let count = 0;
            clients.forEach(c => {
              const ws = Array.isArray(c.websites) ? c.websites : [];
              count += ws.length;
            });
            setWebsitesCount(count || clients.length || 0);
            // Build top websites list (first 5)
            const rows = [];
            clients.forEach(c => {
              const ownerName = c.client_name || c.user_name || c.name || '—';
              (Array.isArray(c.websites) ? c.websites : []).forEach(w => rows.push({
                owner: ownerName,
                url: w.url,
                status: w.status,
                created_at: w.created_at
              }));
            });
            setTopWebsites(rows.slice(0, 5));
          }
        } catch {}

        // Snippets stats (active)
        try {
          const res = await fetch(`${apiBase}/admin/website-snippets/stats`, { headers: { ...authHeaders() } });
          const body = await res.json().catch(() => ({}));
          if (res.ok) setActiveSnippetsCount(body?.by_status?.active || 0);
        } catch {}

        // Tickets (open)
        try {
          const res = await fetch(`${apiBase}/tickets`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
          const body = await res.json().catch(() => ([]));
          if (res.ok && Array.isArray(body)) {
            const open = body.filter(t => String(t.status || '').toLowerCase() === 'open');
            setOpenTicketsCount(open.length || body.length || 0);
          }
        } catch {}

        // Recent activity (flat API)
        try {
          const params = new URLSearchParams({ limit: '5' });
          const res = await fetch(`${apiBase}/admin/website-activity/recent?${params}`, { headers: { ...authHeaders() } });
          const body = await res.json().catch(() => ({}));
          const items = Array.isArray(body?.items) ? body.items : [];
          setActivity(
            items.slice(0, 5).map((a, i) => ({
              id: a.id || i + 1,
              time: a.created_at || new Date().toISOString(),
              text: a.event_type || a.category || a.page_url || a.referrer || 'Activity event',
              user: a.user_name || null,
              website: a.website_url || a.business_website || null,
              type: a.category || a.event_type || null,
              ip: a.ip_address || null,
              path: a.page_url || null
            }))
          );
        } catch {}

        // System metrics (uptime and errors today)
        try {
          const res = await fetch(`${apiBase}/admin/system-metrics`, { headers: { ...authHeaders() } });
          const body = await res.json().catch(() => ({}));
          if (res.ok) {
            setUptimePercent(body?.uptime_percent_last_24h);
            setErrorsToday(body?.errors_today || 0);
          }
        } catch {}

        // Real-time System Health (API + DB)
        try {
          const res = await fetch(`${apiBase}/admin/system-health`, { headers: { ...authHeaders() } });
          const body = await res.json().catch(() => ({}));
          if (res.ok) {
            setApiHealth({ ok: body.api_ok, latencyMs: body.api_latency_ms });
            setDbHealth({ ok: body.db_ok, latencyMs: body.db_latency_ms });
          }
        } catch {}

        // Website Analytics (Last 7 Days)
        try {
          const res = await fetch(`${apiBase}/admin/website-analytics?days=7`, { headers: { ...authHeaders() } });
          const body = await res.json().catch(() => ({ analytics: [] }));
          if (res.ok) {
            const analytics = Array.isArray(body?.analytics) ? body.analytics : [];
            setWebsiteAnalytics(analytics.map(item => ({
              id: item.website_id || item.id,
              website_url: item.website_url || item.url,
              website_type: item.website_type || item.category || 'general',
              owner: item.owner_name || item.client_name || 'Unknown',
              pageviews: item.pageviews || 0,
              unique_visitors: item.unique_visitors || 0,
              bounce_rate: item.bounce_rate || 0,
              avg_session_duration: item.avg_session_duration || 0,
              conversion_rate: item.conversion_rate || 0,
              revenue: item.revenue || 0
            })));
          }
        } catch {}
      } finally {
        setLoading(false);
      }
  }, []);

  useEffect(() => { load(); }, [load]);

  // KPIs
  const kpiCards = useMemo(() => [
    { title: 'Total Users', value: usersCount, icon: <TeamOutlined />, color: '#2563eb', route: '/dashboard/super-admin/users' },
    { title: 'Total Websites', value: websitesCount, icon: <GlobalOutlined />, color: '#16a34a', route: '/dashboard/super-admin/websites' },
    { title: 'Open Tickets', value: openTicketsCount, icon: <AlertOutlined />, color: '#f59e0b', route: '/dashboard/super-admin/support' },
    { title: 'Active Snippets', value: activeSnippetsCount, icon: <CodeOutlined />, color: '#9333ea', route: '/dashboard/super-admin/snippets' },
    { title: 'System Uptime', value: uptimePercent != null ? `${uptimePercent}%` : '—', icon: <CheckCircleOutlined />, color: '#10b981', route: '/dashboard/super-admin/logs' },
    { title: 'Errors Today', value: errorsToday, icon: <BugOutlined />, color: '#ef4444', route: '/dashboard/super-admin/logs' },
  ], [usersCount, websitesCount, openTicketsCount, activeSnippetsCount, uptimePercent, errorsToday]);

  // Website type options
  const websiteTypeOptions = [
    { label: 'All Types', value: 'all' },
    { label: 'E-commerce', value: 'ecommerce' },
    { label: 'Blog', value: 'blog' },
    { label: 'Portfolio', value: 'portfolio' },
    { label: 'Business', value: 'business' },
    { label: 'News', value: 'news' },
    { label: 'Educational', value: 'educational' },
    { label: 'Entertainment', value: 'entertainment' },
    { label: 'Non-profit', value: 'nonprofit' },
    { label: 'Other', value: 'other' }
  ];

  // Filter website analytics by selected type
  const filteredAnalytics = useMemo(() => {
    if (selectedWebsiteType === 'all') {
      return websiteAnalytics;
    }
    return websiteAnalytics.filter(item => item.website_type === selectedWebsiteType);
  }, [websiteAnalytics, selectedWebsiteType]);

  // Placeholder traffic spark bars (could be wired to analytics later)
  const traffic = [1200, 980, 1520, 1360, 1780, 1640, 1900];
  const maxTraffic = Math.max(...traffic);

  // Columns for Top Websites table
  const websiteColumns = [
    { title: 'Owner', dataIndex: 'owner', width: 160 },
    { title: 'URL', dataIndex: 'url' },
    { title: 'Status', dataIndex: 'status', width: 120, render: (s) => <Tag color={s === 'active' ? 'green' : 'default'}>{s}</Tag> },
    { title: 'Created', dataIndex: 'created_at', width: 180, render: (t) => t ? new Date(t).toLocaleString() : '—' },
  ];

  // Columns for Website Analytics table
  const analyticsColumns = [
    {
      title: 'Website',
      dataIndex: 'website_url',
      key: 'website_url',
      render: (url) => (
        <div>
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500 }}>
            {url}
          </a>
        </div>
      )
    },
    {
      title: 'Type',
      dataIndex: 'website_type',
      key: 'website_type',
      width: 120,
      render: (type) => {
        const colors = {
          ecommerce: 'green',
          blog: 'blue',
          portfolio: 'purple',
          business: 'orange',
          news: 'red',
          educational: 'cyan',
          entertainment: 'magenta',
          nonprofit: 'lime',
          other: 'default'
        };
        return <Tag color={colors[type] || 'default'}>{type}</Tag>;
      }
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: 140
    },
    {
      title: 'Page Views',
      dataIndex: 'pageviews',
      key: 'pageviews',
      width: 120,
      render: (value) => value.toLocaleString()
    },
    {
      title: 'Visitors',
      dataIndex: 'unique_visitors',
      key: 'unique_visitors',
      width: 100,
      render: (value) => value.toLocaleString()
    },
    {
      title: 'Bounce Rate',
      dataIndex: 'bounce_rate',
      key: 'bounce_rate',
      width: 120,
      render: (value) => `${value.toFixed(1)}%`
    },
    {
      title: 'Avg. Session',
      dataIndex: 'avg_session_duration',
      key: 'avg_session_duration',
      width: 120,
      render: (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
      }
    },
    {
      title: 'Conversion',
      dataIndex: 'conversion_rate',
      key: 'conversion_rate',
      width: 110,
      render: (value) => `${value.toFixed(2)}%`
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 120,
      render: (value) => value > 0 ? `$${value.toLocaleString()}` : '—'
    }
  ];

  return (
    <div className="sa-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2} style={{ margin: 0 }}>Super Admin Dashboard</Title>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
      </div>

      {/* KPI Cards */}
      <Row gutter={[16, 16]}>
        {kpiCards.map((c) => (
          <Col key={c.title} xs={24} sm={12} md={8} lg={8} xl={8}>
            <Card 
              hoverable 
              style={{ borderRadius: 14, cursor: 'pointer' }} 
              bodyStyle={{ display: 'flex', alignItems: 'center', gap: 12 }}
              onClick={() => navigate(c.route)}
            >
              <div style={{ width: 44, height: 44, borderRadius: 10, background: c.color + '22', color: c.color, display: 'grid', placeItems: 'center', fontSize: 20 }}>
                {c.icon}
              </div>
              <div>
                <Text type="secondary">{c.title}</Text>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{c.value}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
        {/* Traffic Overview */}
        <Col xs={24} md={14}>
          <Card title="Traffic Overview (last 7 days)" style={{ borderRadius: 14 }} loading={loading}>
            <Space size={8} align="end" style={{ width: '100%', height: 140 }}>
              {traffic.map((v, i) => (
                <div key={i} style={{ width: '100%', maxWidth: 36, background: '#e5e7eb', borderRadius: 6, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${Math.max(12, (v / maxTraffic) * 100)}%`, background: '#60a5fa' }} />
                </div>
              ))}
            </Space>
            <div style={{ marginTop: 12, color: '#64748b' }}>Peak: {maxTraffic.toLocaleString()} sessions</div>
          </Card>
        </Col>

        {/* System Health */}
        <Col xs={24} md={10}>
          <Card title="System Health" style={{ borderRadius: 14 }} loading={loading}>
            <Row gutter={16}>
              <Col span={12} style={{ textAlign: 'center' }}>
                <Progress 
                  type="dashboard" 
                  percent={(apiHealth.latencyMs != null) ? Math.max(0, Math.min(100, 100 - Math.min(apiHealth.latencyMs, 1000) / 10)) : 0}
                  size={120} 
                  status={apiHealth.ok ? (apiHealth.latencyMs != null && apiHealth.latencyMs <= 200 ? 'success' : (apiHealth.latencyMs != null && apiHealth.latencyMs <= 500 ? 'normal' : 'exception')) : 'exception'}
                />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">API</Text>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{apiHealth.latencyMs != null ? `${apiHealth.latencyMs} ms` : '—'}</div>
                </div>
              </Col>
              <Col span={12} style={{ textAlign: 'center' }}>
                <Progress 
                  type="dashboard" 
                  percent={(dbHealth.latencyMs != null) ? Math.max(0, Math.min(100, 100 - Math.min(dbHealth.latencyMs, 1000) / 10)) : 0}
                  size={120} 
                  status={dbHealth.ok ? (dbHealth.latencyMs != null && dbHealth.latencyMs <= 200 ? 'success' : (dbHealth.latencyMs != null && dbHealth.latencyMs <= 500 ? 'normal' : 'exception')) : 'exception'}
                />
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Database</Text>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{dbHealth.latencyMs != null ? `${dbHealth.latencyMs} ms` : '—'}</div>
                </div>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col span={12}>
                <Statistic title="API Response" value={apiHealth.latencyMs || 0} suffix="ms" />
              </Col>
              <Col span={12}>
                <Statistic title="DB Response" value={dbHealth.latencyMs || 0} suffix="ms" />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Website Analytics Section */}
      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <BarChartOutlined />
                <span>Website Analytics (Last 7 Days)</span>
              </Space>
            }
            extra={
              <Select
                value={selectedWebsiteType}
                onChange={setSelectedWebsiteType}
                options={websiteTypeOptions}
                style={{ width: 150 }}
                placeholder="Select Type"
              />
            }
            style={{ borderRadius: 14 }}
            loading={loading}
          >
            <Table
              columns={analyticsColumns}
              dataSource={filteredAnalytics}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true }}
              scroll={{ x: 1200 }}
              size="small"
              locale={{
                emptyText: selectedWebsiteType === 'all' 
                  ? 'No website analytics data available' 
                  : `No ${selectedWebsiteType} websites found`
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
        {/* Recent Activity */}
        <Col xs={24} md={12}>
          <Card title="Recent System Activity" style={{ borderRadius: 14 }} loading={loading}>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {activity.map(a => (
                <li key={a.id} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">{new Date(a.time).toLocaleString()}</Text>
                    <div>
                      {a.type && <Tag color="purple" style={{ marginLeft: 8, textTransform: 'capitalize' }}>{a.type}</Tag>}
                      {a.user && <Tag color="blue" style={{ marginLeft: 8 }}>{a.user}</Tag>}
                    </div>
                  </div>
                  <div>{a.text}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {a.website && (
                      <>
                        Website: <a href={a.website} target="_blank" rel="noreferrer">{a.website}</a>
                        {a.path && <> • Path: <code>{a.path}</code></>}
                      </>
                    )}
                    {!a.website && a.path && (
                      <>Path: <code>{a.path}</code></>
                    )}
                    {a.ip && <> • IP: {a.ip}</>}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </Col>

        {/* Top Websites */}
        <Col xs={24} md={12}>
          <Card title="Top Websites" style={{ borderRadius: 14 }} loading={loading}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={topWebsites}
              columns={websiteColumns}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SADashboard;
